// TrustCenter.tsx
import React from "react";

interface TrustPillar {
  title: string;
  description: string;
  items: {
    title: string;
    description: string;
    link?: string;
  }[];
}

interface HyperlinkProps {
  href?: string;
  children: React.ReactNode;
  className?: string;
}

const ExternalLinkIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="currentColor"
    className="inline-block ml-1 mb-1"
    xmlns="http://www.w3.org/2000/svg"
    data-rtl-flip=""
  >
    <path d="M11.3349 10.3301V5.60547L4.47065 12.4707C4.21095 12.7304 3.78895 12.7304 3.52925 12.4707C3.26955 12.211 3.26955 11.789 3.52925 11.5293L10.3945 4.66504H5.66011C5.29284 4.66504 4.99507 4.36727 4.99507 4C4.99507 3.63273 5.29284 3.33496 5.66011 3.33496H11.9999L12.1337 3.34863C12.4369 3.41057 12.665 3.67857 12.665 4V10.3301C12.6649 10.6973 12.3672 10.9951 11.9999 10.9951C11.6327 10.9951 11.335 10.6973 11.3349 10.3301ZM11.333 4.66699L11.3349 4.66797L11.332 4.66504H11.331L11.333 4.66699Z"></path>
  </svg>
);

const Hyperlink: React.FC<HyperlinkProps> = ({
  href,
  children,
  className = "",
}) => {
  if (!href) {
    return <span className={className}>{children}</span>;
  }

  return (
    <a
      href={href}
      className={`inline-flex items-center font-medium text-blue-600 hover:text-blue-800 transition-colors duration-200 ${className}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
      <ExternalLinkIcon />
    </a>
  );
};

const TrustCenter: React.FC = () => {
  const trustPillars: TrustPillar[] = [
    {
      title: "Security & Infrastructure",
      description:
        "We are committed to protecting your data with robust, enterprise-grade security. Our systems are built on secure cloud infrastructure and protected by multiple layers of defense.",
      items: [
        {
          title: "Data Encryption",
          description:
            "We enforce strong encryption for your data both in transit and at rest.",
          link: "/data-encryption",
        },
        {
          title: "Network Security",
          description:
            "Our platform is protected by firewalls, intrusion detection, and proactive monitoring against threats.",
        },
        {
          title: "Availability & Reliability",
          description:
            "Our architecture is designed for high availability to ensure you can access your tools when you need them.",
        },
      ],
    },
    {
      title: "Privacy & Data Governance",
      description:
        "We are stewards of your data, not owners. Your data is processed only according to your instructions and in full compliance with global data protection laws.",
      items: [
        {
          title: "Privacy Policy",
          description: "Learn how we collect, use, and protect personal data.",
          link: "/policy",
        },
        {
          title: "Data Processing Addendum (DPA)",
          description:
            "Our binding commitment to you as your Data Processor under laws like the NDPA and GDPR.",
          link: "/dpa",
        },
        {
          title: "Data Compliance",
          description:
            "We are compliant with the Nigeria Data Protection Act (NDPA) and build our platform to support our customers' compliance with global standards like GDPR.",
        },
      ],
    },
    {
      title: "Access & Accountability",
      description:
        "We ensure that only the right people have access to your data, and we maintain a full record of that access for your security.",
      items: [
        {
          title: "Access Controls & Audit Trails",
          description:
            "See how we manage user permissions, enforce secure authentication, and log all critical activities.",
          link: "/access-controls",
        },
        {
          title: "Internal Controls",
          description:
            "Our internal access policies are strict. 25th & Staffing employees can only access customer data for specific, approved support and maintenance reasons, with all access logged.",
        },
      ],
    },
    {
      title: "Responsible AI",
      description:
        "Fairness and bias mitigation are at the core of our AI. We believe AI should be a tool to reduce bias in hiring, not perpetuate it.",
      items: [
        {
          title: "Responsible AI Policy",
          description:
            "Read our principles on Fairness, Transparency, and keeping a Human-in-the-Loop.",
          link: "/responsible-ai",
        },
      ],
    },
  ];

  return (
    <div className="">
      <div className="">
        {/* Header */}
        <header className="text-center py-6">
          <h1 className="text-[20px] md:text-[35px] text-gray-900 mb-4">
            AI Recruiter Trust Center
          </h1>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            Welcome to the 25th & Staffing Trust Center. We understand that in
            the world of recruitment, data is your most valuable asset. The
            trust of our customers, and in turn, their candidates, is the
            foundation of our business.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            This Trust Center is our central hub for providing transparency into
            our security, privacy, compliance, and reliability practices.
          </p>
        </header>

        {/* Core Pillars Section */}
        <section className="mb-6">
          <h2 className="text-3xl font-semibold text-gray-900 mb-8 text-center">
            Our Core Pillars of Trust
          </h2>

          <div className="space-y-8">
            {trustPillars.map((pillar, index) => (
              <div
                key={pillar.title}
                className="bg-white rounded-[10px] shadow-sm border border-[gray]/10 p-6 hover:shadow-md transition-shadow duration-300"
              >
                <div className="flex items-start mb-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                    <span className="text-blue-600 font-semibold text-sm">
                      {index + 1}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {pillar.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {pillar.description}
                    </p>
                  </div>
                </div>

                <div className="ml-12 space-y-4">
                  {pillar.items.map((item) => (
                    <div
                      key={item.title}
                      className="border-l-2 border-blue-200 pl-4 py-1"
                    >
                      <h4 className="font-medium text-gray-900 mb-1">
                        {item.link ? (
                          <Hyperlink href={item.link}>{item.title}</Hyperlink>
                        ) : (
                          item.title
                        )}
                      </h4>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Section */}
        <section className="bg-white rounded-[10px] shadow-sm border border-[gray]/10 p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4 text-center">
            Have Questions?
          </h2>
          <p className="text-gray-600 text-center mb-6">
            If you have any questions about our security or trust practices,
            please don't hesitate to reach out to our team.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <Hyperlink href="mailto:privacy@25thmail.com" className="text-lg">
              privacy@25thmail.com
            </Hyperlink>
            <span className="text-gray-400 hidden sm:inline">•</span>
            <span className="text-gray-600">
              or your designated Account Executive
            </span>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TrustCenter;
